import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cropper from 'react-easy-crop';
import {
  fetchMembers,
  createMember,
  updateMember,
  deleteMember,
  clearError,
  clearSuccess,
} from '../../data/memberSlice';

/* ── helpers ── */

// Returns the pixel rectangle from a crop + image dimensions
function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height,
      );

      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Canvas is empty')); return; }
        resolve(blob);
      }, 'image/jpeg', 0.92);
    });
    image.addEventListener('error', reject);
    image.src = imageSrc;
  });
}

/* ── Crop modal ── */
const CropModal = ({ src, onDone, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, cap) => setCroppedAreaPixels(cap), []);

  const handleApply = async () => {
    try {
      const blob = await getCroppedImg(src, croppedAreaPixels);
      onDone(blob);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex flex-col items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col" style={{ maxHeight: '90vh' }}>
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h3 className="font-bold text-gray-800">Crop Profile Photo</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>

        {/* Cropper area */}
        <div className="relative bg-gray-900" style={{ height: 340 }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="rect"
            showGrid={true}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Zoom slider */}
        <div className="px-5 py-3 flex items-center gap-3 border-t">
          <span className="text-xs text-gray-500 w-8">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-blue-600"
          />
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition"
          >
            Use this crop
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Members component ── */
const Members = () => {
  const dispatch = useDispatch();
  const { items: members, loading, error, success } = useSelector((s) => s.members);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [formData, setFormData] = useState({ name: '', member_role: '', email: '', image: null });
  const [previewImage, setPreviewImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Crop state
  const [cropSrc, setCropSrc] = useState(null);   // raw file data URL shown in CropModal
  const fileInputRef = useRef(null);

  useEffect(() => { dispatch(fetchMembers()); }, [dispatch]);

  useEffect(() => {
    if (success) {
      setSuccessMessage(currentMember ? 'Member updated successfully!' : 'Member created successfully!');
      setTimeout(() => { setSuccessMessage(''); dispatch(clearSuccess()); }, 3000);
    }
  }, [success, dispatch, currentMember]);

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setTimeout(() => { setErrorMessage(''); dispatch(clearError()); }, 5000);
    }
  }, [error, dispatch]);

  const handleOpenModal = (member = null) => {
    if (member) {
      setCurrentMember(member);
      setFormData({ name: member.name || '', member_role: member.member_role || '', email: member.email || '', image: null });
      setPreviewImage(member.imageData || '');
    } else {
      setCurrentMember(null);
      setFormData({ name: '', member_role: '', email: '', image: null });
      setPreviewImage('');
    }
    setErrorMessage('');
    setCropSrc(null);
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // When file chosen → open crop modal instead of directly setting image
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    // reset input so same file can be re-selected
    e.target.value = '';
  };

  // Called when admin confirms crop
  const handleCropDone = (blob) => {
    const croppedFile = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
    setFormData((prev) => ({ ...prev, image: croppedFile }));
    setPreviewImage(URL.createObjectURL(blob));
    setCropSrc(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.member_role || !formData.email) {
      setErrorMessage('Please fill in all required fields');
      return;
    }
    setIsLoading(true);
    try {
      if (currentMember) {
        await dispatch(updateMember({ memberId: currentMember.id, memberData: formData })).unwrap();
      } else {
        if (!formData.image) { setErrorMessage('Please select and crop a profile image'); setIsLoading(false); return; }
        await dispatch(createMember(formData)).unwrap();
      }
      setIsModalOpen(false);
      setFormData({ name: '', member_role: '', email: '', image: null });
      setPreviewImage('');
      setCurrentMember(null);
    } catch (err) {
      setErrorMessage(err || 'An error occurred while saving the member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMember = (member) => { setCurrentMember(member); setIsDeleteModalOpen(true); };

  const confirmDelete = async () => {
    if (!currentMember) return;
    setIsLoading(true);
    try {
      await dispatch(deleteMember(currentMember.id)).unwrap();
      setIsDeleteModalOpen(false);
      setCurrentMember(null);
    } catch { setErrorMessage('Failed to delete member'); }
    finally { setIsLoading(false); }
  };

  const getImageUrl = (imageData) => {
    if (!imageData) return '/assets/placeholder.jpg';
    if (typeof imageData === 'string') {
      if (imageData.startsWith('data:image')) return imageData;
      return imageData.startsWith('http') ? imageData : `/assets/${imageData}`;
    }
    return '/assets/placeholder.jpg';
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Team Member Management</h1>
        <button
          onClick={() => handleOpenModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg flex items-center transition"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Member
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex justify-between items-center">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage('')} className="font-bold">✕</button>
        </div>
      )}
      {errorMessage && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex justify-between items-center">
          <p>{errorMessage}</p>
          <button onClick={() => setErrorMessage('')} className="font-bold">✕</button>
        </div>
      )}

      {loading && !isLoading && (
        <div className="flex justify-center my-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}

      {/* Members grid */}
      {!loading && members && members.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {members.map((member) =>
            member ? (
              <div key={member.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition">
                <div className="h-48 bg-gray-200 overflow-hidden">
                  <img src={getImageUrl(member.imageData)} alt={member.name || 'Team Member'} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{member.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-600 mb-2 font-semibold">{member.member_role || ''}</p>
                  <p className="text-xs text-gray-500 mb-4 break-all">{member.email || ''}</p>
                  <div className="flex gap-2">
                    <button onClick={() => handleOpenModal(member)} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-2 px-3 rounded transition">Edit</button>
                    <button onClick={() => handleDeleteMember(member)} className="flex-1 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold py-2 px-3 rounded transition">Delete</button>
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>
      )}

      {!loading && (!members || members.length === 0) && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3.75A2.75 2.75 0 011 18.25V5.75A2.75 2.75 0 013.75 3h16.5A2.75 2.75 0 0123 5.75v12.5A2.75 2.75 0 0120.25 21h-5" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No team members found</h3>
          <p className="mt-1 text-gray-500">Add your first team member to get started</p>
        </div>
      )}

      {/* ── Crop modal (shown above form modal) ── */}
      {cropSrc && (
        <CropModal
          src={cropSrc}
          onDone={handleCropDone}
          onCancel={() => { setCropSrc(null); }}
        />
      )}

      {/* ── Form modal ── */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">{currentMember ? 'Edit Member' : 'Add New Member'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter member name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Role *</label>
                <input type="text" name="member_role" value={formData.member_role} onChange={handleInputChange} placeholder="e.g., Director, Manager"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="Enter member email"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required disabled={!!currentMember} />
              </div>

              {/* Image picker */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Profile Image {!currentMember && '*'}
                </label>

                {/* Show circular preview + change button if image already selected */}
                {previewImage ? (
                  <div className="flex items-center gap-4">
                    <img src={previewImage} alt="Cropped preview"
                      className="w-20 h-20 rounded-lg object-cover border-2 border-blue-300 shadow" />
                    <div>
                      <p className="text-xs text-green-600 font-medium mb-1">Image ready ✓</p>
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        className="text-sm text-blue-600 hover:underline">
                        Choose different photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 hover:border-blue-400 rounded-lg py-6 text-gray-500 hover:text-blue-600 transition cursor-pointer">
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">Click to select photo</span>
                    <span className="text-xs text-gray-400">You'll be able to crop it next</span>
                  </button>
                )}

                {/* Hidden file input */}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition disabled:opacity-50">
                  {isLoading ? 'Saving...' : currentMember ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-lg font-bold text-gray-900">Confirm Delete</h2>
              <button onClick={() => setIsDeleteModalOpen(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-6">
              <p className="text-gray-700">Are you sure you want to delete <strong>{currentMember?.name}</strong>? This action cannot be undone.</p>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button onClick={confirmDelete} disabled={isLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition disabled:opacity-50">
                  {isLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Members;
